import { call, delay, put, takeLatest } from "redux-saga/effects";
// for toast
import { toast } from "react-toastify";

import { UserDropdown, UserDropdownActionType } from "../../actionTypes/userDropdownActionTypes";
import {
  getBankDropdownListApi,
  getBranchDropdownListApi,
  getCompanyDropdownListApi,
  getDepartmentDropdownListApi,
  getPackageTypeNameAsValueDropdownListApi,
  getPermissionDropdownListApi,
  getSectionDropdownListApi,
  getUserDropdownListApi,
  getVehicleDropdownListApi,
} from "../requests/dropdownRequest";
import { setUserListDropdown } from "../../actions/userDropdownActions";
import { BranchDropdown, BranchDropdownActionType } from "../../actionTypes/master/branchDropdownActionTypes";
import { setBranchListDropdown } from "../../actions/master/branchDropdownActions";
import { DepartmentDropdown, DepartmentDropdownActionType } from "../../actionTypes/master/departmentDropdownActionTypes";
import { setDepartmentListDropdown } from "../../actions/master/departmentDropdownActions";
import { BankDropdown, BankDropdownActionType } from "../../actionTypes/master/bankDropdownActionTypes";
import { setBankListDropdown } from "../../actions/master/bankDropdownActions";
import { VehicleDropdown, VehicleDropdownActionType } from "../../actionTypes/master/vehicleTypeDropdownActionTypes";
import { setVehicleListDropdown } from "../../actions/master/vehicleDropdownActions";
import { startLoader } from "../../../utils/commonFunctions";
import { stopLoader } from "../../../utils/commonFunctions";
import { PermissionActionTypes, PermissionActions } from "../../actionTypes/permissions/permissionTypes";
import { setPermissionDropdownList, setPermissionDropdownLoading } from "../../actions/permissions/permissionActions";
import { CompanyDropdown, CompanyDropdownActionType } from "../../actionTypes/master/companyDropdownActionTypes";
import { setCompanyListDropdown } from "../../actions/master/companyDropdownActions";
import { setPackageTypeNameAsValueDropdownList, setPackageTypeNameAsValueDropdownLoading } from "../../actions/master/packageAction";
import { PackageActionTypes, PackageActions } from "../../actionTypes/master/packageTypes";
import { SectionDropdown, SectionDropdownActionType } from "../../actionTypes/permissions/sectionDropdownActionTypes";
import { setSectionListDropdown } from "../../actions/permissions/sectionDropdownActions";
import { StateDropdown, StateDropdownActionType } from "../../actionTypes/master/StateDropdownActionType";
import { AreaCodeDropdown, AreaCodeDropdownActionType } from "../../actionTypes/master/AreaCodeDropdownActionType";
import { getStateDropdownListApi } from "../requests/dropdownRequest";
import { getAreaCodeListApi } from "../requests/master/areaCodeRequests";
import { setAreaCodeDropdownList, setStateDropdownList } from "../../actions/dropdownActions";


function* getStateDropdownListHandler(action: StateDropdownActionType) {
  yield delay(300);
  try {
    yield put({ type: StateDropdown.SET_STATE_DROPDOWN_LOADING, payload: true });
    const { data } = yield call(getStateDropdownListApi, action.payload);
    if (data.status) {
      const rows = data?.data?.rows ?? data?.data?.rows ?? [];
      const dropdownRows = rows
        .map((r: any) => {
          // Different APIs sometimes return different shapes; normalize to {value,label}
          const valueCandidate =
            r?.value ??
            r?.id ??
            r?.state_id ??
            r?.stateId ??
            r?.state?.id ??
            r?.state?.state_id ??
            undefined;

          const labelCandidate = r?.label ?? r?.state_name ?? r?.stateName ?? r?.state ?? r?.name ?? "";

          const valueNumber = Number(valueCandidate);
          if (!Number.isFinite(valueNumber) || valueNumber <= 0) return null;
          if (!String(labelCandidate).trim()) return null;

          return { value: valueNumber, label: String(labelCandidate).trim() };
        })
        .filter(Boolean);

      yield put(setStateDropdownList(dropdownRows as any[]));
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message, {
      position: "top-right",
    });
  } finally {
    yield put({ type: StateDropdown.SET_STATE_DROPDOWN_LOADING, payload: false });
  }
}

function* getAreaCodeDropdownListHandler(action: AreaCodeDropdownActionType) {
  yield delay(300);
  try {
    yield put({ type: AreaCodeDropdown.SET_AREA_CODE_DROPDOWN_LOADING, payload: true });
    const { data } = yield call(getAreaCodeListApi, action.payload);
    if (data.status) {
      const rows = data?.data?.rows ?? [];
      const dropdownRows = rows.map((r: any) => ({
        value: r.id,
        label: r.area_code ? `${r.area_code}${r.area_name ? ` - ${r.area_name}` : ""}` : r.area_name ?? String(r.id),
      }));
      yield put(setAreaCodeDropdownList(dropdownRows));
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message, {
      position: "top-right",
    });
  } finally {
    yield put({ type: AreaCodeDropdown.SET_AREA_CODE_DROPDOWN_LOADING, payload: false });
  }
}

function* getUserDropdownListHandler(action: UserDropdownActionType) {
  yield delay(300);
  try {
    yield startLoader("search");

    const { data } = yield call(getUserDropdownListApi, action.payload);

    if (data.status) {
      yield put(setUserListDropdown([])); // emptying the previous data in the dropdown
      yield put(setUserListDropdown(data?.data?.rows));
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message, {
      position: "top-right",
    });
  } finally {
    yield stopLoader("search");
  }
}

function* getBranchDropdownListHandler(action: BranchDropdownActionType) {
  yield delay(500);
  try {
    yield put({ type: BranchDropdown.SET_BRANCH_DROPDOWN_LOADING, payload: true });

    const { data } = yield call(getBranchDropdownListApi, action.payload);
    if (data.status) {
      yield put(setBranchListDropdown([])); // emptying the previous data in the dropdown
      yield put(setBranchListDropdown(data?.data?.rows));
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message, {
      position: "top-right",
    });
  } finally {
    yield put({ type: BranchDropdown.SET_BRANCH_DROPDOWN_LOADING, payload: false });
  }
}

function* getDepartmentDropdownListHandler(action: DepartmentDropdownActionType) {
  yield delay(500);
  try {
    yield put({ type: DepartmentDropdown.SET_DEPARTMENT_DROPDOWN_LOADING, payload: true });

    const { data } = yield call(getDepartmentDropdownListApi, action.payload);
    if (data.status) {
      yield put(setDepartmentListDropdown([])); // emptying the previous data in the dropdown
      yield put(setDepartmentListDropdown(data?.data?.rows));
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message, {
      position: "top-right",
    });
  } finally {
    yield put({ type: DepartmentDropdown.SET_DEPARTMENT_DROPDOWN_LOADING, payload: false });
  }
}

function* getSectionDropdownListHandler(action: SectionDropdownActionType) {
  if(action.payload.search){
    yield delay(500);
  }
  try {
    yield put({ type: SectionDropdown.SET_SECTION_DROPDOWN_LOADING, payload: true });

    const { data } = yield call(getSectionDropdownListApi, action.payload);
    if (data.status) {
      const allOption = [{ value: 0, label: "All"}]
      yield put(setSectionListDropdown(allOption.concat(data?.data?.rows)));
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message, {
      position: "top-right",
    });
  } finally {
    yield put({ type: SectionDropdown.SET_SECTION_DROPDOWN_LOADING, payload: false });
  }
}

function* getBankDropdownListHandler(action: BankDropdownActionType) {
  yield delay(500);
  try {
    yield put({ type: BankDropdown.SET_BANK_DROPDOWN_LOADING, payload: true });

    const { data } = yield call(getBankDropdownListApi, action.payload);
    if (data.status) {
      yield put(setBankListDropdown([])); // emptying the previous data in the dropdown
      yield put(setBankListDropdown(data?.data?.rows));
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message, {
      position: "top-right",
    });
  } finally {
    yield put({ type: BankDropdown.SET_BANK_DROPDOWN_LOADING, payload: false });
  }
}

function* getVehicleDropdownListHandler(action: VehicleDropdownActionType) {
  yield delay(500);
  try {
    yield put({ type: VehicleDropdown.SET_VEHICLE_DROPDOWN_LOADING, payload: true });

    const { data } = yield call(getVehicleDropdownListApi, action.payload);
    if (data.status) {
      yield put(setVehicleListDropdown([])); // emptying the previous data in the dropdown
      yield put(setVehicleListDropdown(data?.data?.rows));
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message, {
      position: "top-right",
    });
  } finally {
    yield put({ type: VehicleDropdown.SET_VEHICLE_DROPDOWN_LOADING, payload: false });
  }
}


function* getPermissionDropdownListHandler(action: PermissionActionTypes) {
  yield delay(500);
  try {
    yield put(setPermissionDropdownLoading(true));

    const { data } = yield call(getPermissionDropdownListApi, action.payload);
    if (data.status) {
      yield put(setPermissionDropdownList([])); // emptying the previous data in the dropdown
      yield put(setPermissionDropdownList(data?.data?.rows));
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message, {
      position: "top-right",
    });
  } finally {
    yield put(setPermissionDropdownLoading(true));
  }
}


function* getCompanyDropdownListHandler(action: CompanyDropdownActionType) {
  yield delay(500);
  try {
    yield put({ type: CompanyDropdown.SET_COMPANY_DROPDOWN_LOADING, payload: true });

    const { data } = yield call(getCompanyDropdownListApi, action.payload);
    if (data.status) {
      yield put(setCompanyListDropdown([])); // emptying the previous data in the dropdown
      yield put(setCompanyListDropdown(data?.data?.rows));
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message, {
      position: "top-right",
    });
  } finally {
    yield put({ type: CompanyDropdown.SET_COMPANY_DROPDOWN_LOADING, payload: false });
  }
}


function* getPackageTypeNameAsValueDropdownListHandler(action: PackageActionTypes) {
  yield delay(500);
  try {
    yield put(setPackageTypeNameAsValueDropdownLoading(true));

    const { data } = yield call(getPackageTypeNameAsValueDropdownListApi, action.payload);
    if (data.status) {
      yield put(setPackageTypeNameAsValueDropdownList([])); // emptying the previous data in the dropdown
      yield put(setPackageTypeNameAsValueDropdownList(data?.data?.rows));
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message, {
      position: "top-right",
    });
  } finally {
    yield put(setPackageTypeNameAsValueDropdownLoading(true));
  }
}


// dropdown Saga
export default function* dropdownSaga() {
  yield takeLatest(UserDropdown.GET_USER_DROPDOWN_LIST, getUserDropdownListHandler);
  yield takeLatest(BranchDropdown.GET_BRANCH_DROPDOWN_LIST, getBranchDropdownListHandler);
  yield takeLatest(DepartmentDropdown.GET_DEPARTMENT_DROPDOWN_LIST, getDepartmentDropdownListHandler);
  yield takeLatest(SectionDropdown.GET_SECTION_DROPDOWN_LIST, getSectionDropdownListHandler);
  yield takeLatest(BankDropdown.GET_BANK_DROPDOWN_LIST, getBankDropdownListHandler);
  yield takeLatest(VehicleDropdown.GET_VEHICLE_DROPDOWN_LIST, getVehicleDropdownListHandler);
  yield takeLatest(PermissionActions.GET_PERMISSION_DROPDOWN_LIST, getPermissionDropdownListHandler);
  yield takeLatest(CompanyDropdown.GET_COMPANY_DROPDOWN_LIST, getCompanyDropdownListHandler);
  yield takeLatest(PackageActions.GET_PACKAGE_TYPE_NAME_AS_VALUE_DROPDOWN_LIST, getPackageTypeNameAsValueDropdownListHandler);
  yield takeLatest(StateDropdown.GET_STATE_DROPDOWN_LIST, getStateDropdownListHandler);
  yield takeLatest(AreaCodeDropdown.GET_AREA_CODE_DROPDOWN_LIST, getAreaCodeDropdownListHandler);
}
